// Overnight ingestion is normal: include the whole London calendar day.
export const ADDED_TODAY_CUTOFF_HOUR = 0;
export const ADDED_TODAY_TIMEZONE = 'Europe/London';

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const londonDateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: ADDED_TODAY_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

const londonTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: ADDED_TODAY_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

const getLondonDateKey = (value) => {
  const parts = londonDateFormatter.formatToParts(value);
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;

  if (!year || !month || !day) return null;
  return `${year}-${month}-${day}`;
};

const getLondonMinutes = (value) => {
  const parts = londonTimeFormatter.formatToParts(value);
  const hourValue = parts.find(part => part.type === 'hour')?.value;
  const minuteValue = parts.find(part => part.type === 'minute')?.value;

  const hours = Number.parseInt(hourValue ?? '0', 10);
  const minutes = Number.parseInt(minuteValue ?? '0', 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return Number.NaN;
  }

  return hours * 60 + minutes;
};

export const isScrapedDateAddedToday = (scrapedDate, referenceDate = new Date(), cutoffHour = ADDED_TODAY_CUTOFF_HOUR) => {
  if (!scrapedDate) return false;

  const parsedDate = new Date(scrapedDate);
  if (!isValidDate(parsedDate)) return false;

  const comparisonDate = isValidDate(referenceDate) ? new Date(referenceDate) : new Date();

  const scrapedKey = getLondonDateKey(parsedDate);
  const referenceKey = getLondonDateKey(comparisonDate);

  if (!scrapedKey || !referenceKey || scrapedKey !== referenceKey) {
    return false;
  }

  const scrapedMinutes = getLondonMinutes(parsedDate);
  if (Number.isNaN(scrapedMinutes)) {
    return false;
  }

  const cutoffMinutes = Number(cutoffHour) * 60;
  if (scrapedMinutes < cutoffMinutes) {
    return false;
  }

  return true;
};

export const isScrapedDateAddedYesterday = (scrapedDate, referenceDate = new Date(), cutoffHour = ADDED_TODAY_CUTOFF_HOUR) => {
  const yesterday = new Date(isValidDate(referenceDate) ? referenceDate : new Date());
  yesterday.setDate(yesterday.getDate() - 1);
  return isScrapedDateAddedToday(scrapedDate, yesterday, cutoffHour);
};

export const isShortLetFeedRecord = (app) => {
  if (!app || typeof app !== 'object') return false;
  if (app.record_type === 'short_term_let' || app.data_source === 'short_term_let') {
    return true;
  }
  return String(app.category || '').trim().toLowerCase() === 'short_term_lets';
};

export const getPlanningFeedAddedDate = (app) => {
  if (!app || typeof app !== 'object') return null;
  return app.created_at || app.scraped_date || null;
};

export const isPlanningFeedAddedToday = (app, referenceDate = new Date(), cutoffHour = ADDED_TODAY_CUTOFF_HOUR) => {
  if (isShortLetFeedRecord(app)) return false;
  return isScrapedDateAddedToday(getPlanningFeedAddedDate(app), referenceDate, cutoffHour);
};

export const isPlanningFeedAddedYesterday = (app, referenceDate = new Date(), cutoffHour = ADDED_TODAY_CUTOFF_HOUR) => {
  if (isShortLetFeedRecord(app)) return false;
  return isScrapedDateAddedYesterday(getPlanningFeedAddedDate(app), referenceDate, cutoffHour);
};

export default isScrapedDateAddedToday;
